// src/pages/admin.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

/* ============================================================
   Admin — set / update purchase pricing for USWD and GHSX.

   Backend (routes/adminRoute.js):
     POST /api/admin/login       { code }            -> sets HttpOnly cookie "adminAuthToken"
     POST /api/admin/logout                          -> clears the cookie
     GET  /api/admin/rates                           -> { ghsx, uswd }  (null when not set yet)
     PUT  /api/admin/uswd-rate   { rate, minPurchaseAmount }
     PUT  /api/admin/ghsx-rate   { ghsxToGhsRate, ghsPerUsdt, minPurchaseAmount }

   GHSX is configured with three values:
     - ghsxToGhsRate     GHS per 1 GHSX   (e.g. 0.50)
     - ghsPerUsdt        GHS per 1 USDT   (e.g. 11.95)
     - minPurchaseAmount minimum, in GHSX (e.g. 2)

   Auth is cookie-based: the JWT is never visible to JavaScript, so
   nothing is stored client-side. Every request sends the cookie via
   withCredentials, and "am I logged in?" is answered by asking the
   server (GET /rates) when the page opens.

   The PUT endpoints upsert, so "set" (first time) and "update" use the
   same call. They require ALL fields every time, so each form is
   pre-filled with the current values and always sends every field.
============================================================ */

const MIN_VALUE = 0.000001; // must match the validators in adminRoute.js

// 0.5 -> "0.5", 1.2500 -> "1.25" (max 8 decimals, trailing zeros trimmed)
const fmt = (n) => String(Number(Number(n).toFixed(8)));

// send the HttpOnly admin cookie with every request
const withCreds = { withCredentials: true };

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

const isAuthError = (err) =>
  err?.response?.status === 401 || err?.response?.status === 403;

/* ------------------------------------------------------------
   Field config per asset.
   `key` is both the property on the saved data and the key sent
   to the backend, so it must match adminRoute.js exactly.
------------------------------------------------------------ */
const USWD_FIELDS = [
  { key: "rate", label: "Rate", unit: "USDT per 1 USWD", placeholder: "1.00" },
  { key: "minPurchaseAmount", label: "Minimum purchase amount", unit: "USWD", placeholder: "2" },
];

const GHSX_FIELDS = [
  { key: "ghsxToGhsRate", label: "GHS per 1 GHSX", unit: "GHS per 1 GHSX", placeholder: "0.50" },
  { key: "ghsPerUsdt", label: "GHS per 1 USDT", unit: "GHS per 1 USDT", placeholder: "11.95" },
  { key: "minPurchaseAmount", label: "Minimum purchase amount", unit: "GHSX", placeholder: "2" },
];

/* ------------------------------------------------------------
   One card per asset (USWD / GHSX)
------------------------------------------------------------ */
function RateCard({ title, fields, data, onSave }) {
  const isNew = !data;

  const emptyForm = useCallback(
    () => Object.fromEntries(fields.map((f) => [f.key, ""])),
    [fields]
  );

  const [form, setForm]             = useState(emptyForm);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [message, setMessage]       = useState(null); // { type: "ok" | "error", text }

  // keep the form in sync with the saved values
  useEffect(() => {
    setForm(
      data
        ? Object.fromEntries(
            fields.map((f) => [
              f.key,
              data[f.key] !== undefined && data[f.key] !== null ? fmt(data[f.key]) : "",
            ])
          )
        : emptyForm()
    );
    setConfirming(false);
  }, [data, fields, emptyForm]);

  const numOf = (key) => Number(form[key]);
  const validOf = (key) =>
    form[key] !== "" && Number.isFinite(numOf(key)) && numOf(key) >= MIN_VALUE;

  const allValid = fields.every((f) => validOf(f.key));

  const unchanged =
    !isNew && fields.every((f) => numOf(f.key) === Number(data[f.key]));

  const canSubmit = allValid && !unchanged && !saving;

  const onDecimalChange = (key) => (e) => {
    const raw = e.target.value;
    if (raw === "" || /^\d*\.?\d{0,8}$/.test(raw)) {
      setForm((prev) => ({ ...prev, [key]: raw }));
      setConfirming(false);
      setMessage(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = Object.fromEntries(fields.map((f) => [f.key, numOf(f.key)]));
      await onSave(payload);
      setMessage({
        type: "ok",
        text: isNew ? `${title} pricing set.` : `${title} pricing updated.`,
      });
    } catch (err) {
      setMessage({ type: "error", text: errorMessage(err, "Unable to save pricing.") });
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  const inputClass = (invalid) =>
    `w-full rounded-lg border px-4 py-2.5 text-sm bg-white text-neutral-900
     focus:outline-none focus:ring-2 dark:bg-neutral-900 dark:text-white
     ${invalid
       ? "border-red-400 focus:ring-red-400"
       : "border-neutral-300 focus:ring-orange-400 dark:border-neutral-600"}`;

  return (
    <section className="rounded-xl border bg-white p-5 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
      <div className="mb-4 flex items-start justify-between gap-3">
        <h2 className="text-base font-semibold text-orange-400">{title}</h2>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
            isNew
              ? "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
              : "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
          }`}
        >
          {isNew ? "Not set" : "Active"}
        </span>
      </div>

      {/* Current values */}
      <div className="mb-5 divide-y rounded-xl border bg-neutral-50 text-sm dark:divide-neutral-700 dark:border-neutral-700 dark:bg-neutral-900">
        {fields.map((f) => (
          <div key={f.key} className="flex justify-between px-4 py-3">
            <span className="text-neutral-500">Current {f.label.toLowerCase()}</span>
            <strong>
              {isNew || data[f.key] === undefined || data[f.key] === null
                ? "—"
                : `${fmt(data[f.key])} ${f.key === "minPurchaseAmount" ? f.unit : ""}`.trim()}
            </strong>
          </div>
        ))}
        {!isNew && data.updatedAt && (
          <div className="flex justify-between px-4 py-3 text-xs">
            <span className="text-neutral-500">Last updated</span>
            <span>{new Date(data.updatedAt).toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Form */}
      <div className="flex flex-col gap-4">
        {fields.map((f) => {
          const invalid = form[f.key] !== "" && !validOf(f.key);
          return (
            <div key={f.key}>
              <label className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-white">
                {isNew ? "Set" : "New"} {f.label.toLowerCase()} ({f.unit})
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder={f.placeholder}
                value={form[f.key] ?? ""}
                onChange={onDecimalChange(f.key)}
                className={inputClass(invalid)}
              />
              {invalid && (
                <p className="mt-1 text-xs text-red-500">
                  Must be at least {MIN_VALUE}.
                </p>
              )}
            </div>
          );
        })}

        {message && (
          <p
            className={`text-xs ${
              message.type === "ok" ? "text-green-600" : "text-red-500"
            }`}
          >
            {message.text}
          </p>
        )}

        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={!canSubmit}
            className="rounded-lg bg-orange-400 px-4 py-2.5 text-sm font-semibold text-white
                       hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isNew ? "Set pricing" : "Update pricing"}
          </button>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
            <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">
              This takes effect immediately for new purchases.
            </p>
            <ul className="mb-3 space-y-1 text-xs text-amber-700 dark:text-amber-300">
              {fields.map((f) => (
                <li key={f.key}>
                  {f.label}:{" "}
                  {isNew
                    ? fmt(numOf(f.key))
                    : `${fmt(data[f.key])} → ${fmt(numOf(f.key))}`}
                </li>
              ))}
            </ul>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={saving}
                className="flex-1 rounded-lg border py-2 text-sm font-semibold
                           hover:bg-white disabled:opacity-40 dark:border-neutral-600 dark:hover:bg-neutral-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 rounded-lg bg-orange-400 py-2 text-sm font-semibold text-white
                           hover:bg-orange-500 disabled:opacity-40"
              >
                {saving ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------
   Page
------------------------------------------------------------ */
export default function Admin() {
  // null = checking for an existing session, false = logged out, true = logged in
  const [authed, setAuthed] = useState(null);

  // login
  const [code, setCode]               = useState("");
  const [loginError, setLoginError]   = useState(null);
  const [loggingIn, setLoggingIn]     = useState(false);

  // rates
  const [rates, setRates]             = useState({ uswd: null, ghsx: null });
  const [loading, setLoading]         = useState(false);
  const [loadError, setLoadError]     = useState(null);

  const resetSession = useCallback((reason = null) => {
    setAuthed(false);
    setRates({ uswd: null, ghsx: null });
    setLoginError(reason);
  }, []);

  const logout = async () => {
    try {
      await axios.post("/api/admin/logout", null, withCreds);
    } catch {
      /* cookie may already be gone — still reset the UI */
    }
    resetSession();
  };

  // Loads the rates; also doubles as the "is there a valid session?" check.
  // Returns true on success.
  const loadRates = useCallback(
    async ({ initial = false } = {}) => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await axios.get("/api/admin/rates", withCreds);
        setRates({ uswd: res.data?.uswd ?? null, ghsx: res.data?.ghsx ?? null });
        setAuthed(true);
        return true;
      } catch (err) {
        if (isAuthError(err)) {
          // no / expired session cookie
          resetSession(
            initial ? null : "Your admin session has expired. Please log in again."
          );
        } else if (initial) {
          resetSession(errorMessage(err, "Unable to reach the server."));
        } else {
          setLoadError(errorMessage(err, "Unable to load purchase rates."));
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [resetSession]
  );

  // on open: restore an existing session if the cookie is still valid
  useEffect(() => {
    loadRates({ initial: true });
  }, [loadRates]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setLoginError("Enter the admin code.");
      return;
    }
    setLoggingIn(true);
    setLoginError(null);
    try {
      await axios.post("/api/admin/login", { code: code.trim() }, withCreds);
      setCode("");

      // the server set the cookie; confirm it is accepted by loading the rates
      const ok = await loadRates({ initial: true });
      if (!ok) {
        setLoginError(
          "Logged in, but the session cookie was not accepted. The admin cookie is HTTPS-only — check your connection and cookie settings."
        );
      }
    } catch (err) {
      setLoginError(errorMessage(err, "Unable to log in."));
    } finally {
      setLoggingIn(false);
    }
  };

  // kind: "uswd" | "ghsx"  — throws on failure so the card can show the error
  const saveRate = async (kind, values) => {
    try {
      const res = await axios.put(`/api/admin/${kind}-rate`, values, withCreds);
      setRates((prev) => ({ ...prev, [kind]: res.data?.[kind] ?? prev[kind] }));
    } catch (err) {
      if (isAuthError(err)) {
        resetSession("Your admin session has expired. Please log in again.");
      }
      throw err;
    }
  };

  /* ---------- CHECKING SESSION ---------- */
  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 text-sm text-neutral-500 dark:bg-neutral-900">
        Loading…
      </div>
    );
  }

  /* ---------- LOGIN VIEW ---------- */
  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 dark:bg-neutral-900">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm rounded-2xl border bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
        >
          <h1 className="mb-1 text-lg font-bold text-orange-400">Admin</h1>
          <p className="mb-5 text-xs text-neutral-500 dark:text-neutral-400">
            Enter your admin code to manage purchase rates.
          </p>

          <input
            type="password"
            autoComplete="off"
            placeholder="Admin code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setLoginError(null);
            }}
            className="mb-3 w-full rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm
                       text-neutral-900 focus:outline-none focus:ring-2 focus:ring-orange-400
                       dark:border-neutral-600 dark:bg-neutral-900 dark:text-white"
          />

          {loginError && <p className="mb-3 text-xs text-red-500">{loginError}</p>}

          <button
            type="submit"
            disabled={loggingIn || !code.trim()}
            className="w-full rounded-lg bg-orange-400 py-2.5 text-sm font-semibold text-white
                       hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loggingIn ? "Logging in…" : "Log in"}
          </button>
        </form>
      </div>
    );
  }

  /* ---------- RATES VIEW ---------- */
  return (
    <div className="h-screen overflow-y-auto overscroll-contain bg-neutral-50 px-4 py-8 text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100">
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-5">
        <header className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold">Purchase rates</h1>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => loadRates()}
              disabled={loading}
              className="rounded-lg border px-3 py-2 text-xs font-semibold hover:bg-white
                         disabled:opacity-50 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              onClick={logout}
              className="rounded-lg border px-3 py-2 text-xs font-semibold hover:bg-white
                         dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              Log out
            </button>
          </div>
        </header>

        {loadError && (
          <div className="rounded-lg border border-red-300 px-4 py-3 text-xs text-red-500">
            {loadError}
            <button type="button" onClick={() => loadRates()} className="ml-2 font-semibold underline">
              Retry
            </button>
          </div>
        )}

        {loading && !rates.uswd && !rates.ghsx ? (
          <div className="text-sm text-neutral-500">Loading rates…</div>
        ) : (
          <>
            <RateCard
              title="USWD"
              fields={USWD_FIELDS}
              data={rates.uswd}
              onSave={(values) => saveRate("uswd", values)}
            />
            <RateCard
              title="GHSX"
              fields={GHSX_FIELDS}
              data={rates.ghsx}
              onSave={(values) => saveRate("ghsx", values)}
            />
          </>
        )}
      </main>
    </div>
  );
}